import { z } from "zod";

export enum PaymentMethod {
	CREDITCARD = "CREDIT_CARD",
	DIRECTDEBIT = "DIRECT_DEBIT",
}

export enum PaymentFrequency {
	MONTHLY = "MONTHLY",
	ANNUALY = "ANNUALY",
}

export enum PaymentStatus {
	PENDING = "PENDING",
	SUCCESS = "SUCCESS",
	FAILED = "FAILED",
}

export enum PaymentType {
	CARD = "card",
	IBAN = "sepa_debit",
}

export const PaymentFrequencySchema = z.nativeEnum(PaymentFrequency, {
	required_error: "Veuillez choisir la périodicité de paiement.",
	invalid_type_error: "Veuillez choisir la périodicité de paiement.",
});

export const PaymentMethodSchema = z.nativeEnum(PaymentMethod, {
	required_error: "Veuillez choisir une méthode de paiement.",
});

export const PaymentTypeSchema = z.nativeEnum(PaymentType, {
	required_error: "Veuillez choisir une méthode de paiement.",
});

export const IBANMethodSchema = z.string().optional();

function validateIBANChecksum(iban: string): boolean {
	const trimmedIban: string = iban.replace(/ /g, "");
	const rearranged: string = trimmedIban.slice(4) + trimmedIban.slice(0, 4);

	const numericIban: string = rearranged
		.split("")
		.map((char) => parseInt(char, 36))
		.join("");

	let remainder = 0;
	for (let i = 0; i < numericIban.length; i++) {
		remainder = (remainder * 10 + Number(numericIban[i])) % 97;
	}

	return remainder === 1;
}

const expirationDateSchema = z
	.string()
	.trim()
	.regex(
		/^(0[1-9]|1[0-2])\/\d{2}$/,
		"Le format de la date d'expiration doit être MM/AA",
	)
	.refine((date) => isFutureDate(date), {
		message: "Votre carte de crédit a expirée",
	});

// Function to check if the expiration date is in the future
const isFutureDate = (date: string): boolean => {
	const [month, year] = date.split("/").map(Number);
	const currentYear = new Date().getFullYear() % 100; // Get last two digits of current year
	const currentMonth = new Date().getMonth() + 1; // Months are 0-based in JS

	return (
		year! > currentYear || (year === currentYear && month! >= currentMonth)
	);
};

const IBANSchema = z.string().refine((IBAN) => {
	if (
		!new RegExp(/^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/).test(IBAN.replace(/ /g, ""))
	) {
		return false;
	}
	if (!validateIBANChecksum(IBAN)) {
		return false;
	}
	return true;
}, "Veuillez saisir un IBAN valide.");

export const PaymentFormCreditSchema = z.object({
	paymentFrequency: PaymentFrequencySchema,
	paymentMethod: z.literal(PaymentMethod.CREDITCARD),
	holder: z
		.string()
		.min(1, "Veuillez saisir le titulaire de la carte de crédit"),
});
export const PaymentFormDebitSchema = z.object({
	paymentFrequency: PaymentFrequencySchema,
	paymentMethod: z.literal(PaymentMethod.DIRECTDEBIT),
	IBAN: IBANSchema,
	BIC: z.string(),
	bankName: z.string(),
	holder: z.string().min(1, "Veuillez saisir le titulaire du compte bancaire"),
});

export const PaymentFormSchema = z
	.object({
		paymentFrequency: PaymentFrequencySchema,
		paymentMethod: PaymentMethodSchema,
		IBAN: z.string().optional(),
		BIC: z.string().optional(),
		bankName: z.string().optional(),
		holder: z.string().min(1, "Veuillez saisir le nom du titulaire"),
	})
	.superRefine((data, ctx) => {
		const schema =
			data.paymentMethod === PaymentMethod.CREDITCARD
				? PaymentFormCreditSchema
				: PaymentFormDebitSchema;
		const form = schema.safeParse(data);

		form.error?.errors.forEach((error) => {
			ctx.addIssue(error);
		});
	});

export const CardDetailsSchema = z
	.object({
		card_number: z.string().min(1),
		expDate: expirationDateSchema,
		exp_month: z.number().optional(),
		exp_year: z.number().optional(),
		cvc: z.number(),
	})
	.transform((data) => {
		const [exp_month, exp_year] = data.expDate.split("/").map(Number);
		return {
			card_number: data.card_number,
			exp_month: exp_month!,
			exp_year: exp_year!,
			cvc: data.cvc,
		};
	});

export const PaymentSchema = z
	.object({
		id_opp: z.string(),
		id_affaire: z.string().nullable().default(null),
		paymentFrequency: PaymentFrequencySchema,
		holder: z.string(),
		IBAN: z.string().optional(),
		card: z.string().optional(),
		BIC: z.string().optional(),
		bankName: z.string().optional(),
		paymentType: PaymentTypeSchema,
	})
	.refine(
		(data) => {
			if (
				(!data.IBAN && !data.card) ||
				(data.card && data.paymentType === PaymentType.IBAN) ||
				(data.IBAN && data.paymentType === PaymentType.CARD)
			) {
				return false;
			}

			if (data.card && data.paymentFrequency === PaymentFrequency.MONTHLY) {
				return false;
			}

			return true;
		},
		{
			message: "Invalid payment infos",
		},
	);

export const ClientDetailsSchema = z.object({
	firstName: z.string().min(1),
	lastName: z.string().min(1),
	email: z.string().min(1),
});

export const PricingFormSchema = z.object({
	price: z.number().min(0, "Le prix doit être supérieur à 0"),
	email: z.string().email().min(1),
	phone: z.string().min(1),
	submissionId: z.string().min(1),
	product: z.string().min(1),
	customer: z.string().min(1),
	firstName: z.string().min(1),
	lastName: z.string().min(1),
	agency: z.string().min(1),
	hotel: z.string().min(1),
	startDate: z.string().optional(),
	endDate: z.string().optional(),
});

export const PaymentModelSchema = PricingFormSchema.extend({
	url: z.string().url(),
	createdAt: z.date(),
});

export type PaymentFormDebitDto = z.infer<typeof PaymentFormDebitSchema>;
export type PaymentFormCreditDto = z.infer<typeof PaymentFormCreditSchema>;

export type PaymentFrequencyDto = z.infer<typeof PaymentFrequencySchema>;
export type PaymentMethodDto = z.infer<typeof PaymentMethodSchema>;
export type PaymentFormData = z.infer<typeof PaymentFormSchema>;
export type PaymentDTO = z.infer<typeof PaymentSchema>;
export type ClientDetailsDTO = z.infer<typeof ClientDetailsSchema>;
export type PricingFormDto = z.infer<typeof PricingFormSchema>;
export type PaymentModelDto = z.infer<typeof PaymentModelSchema>;
